import React, { useState, useEffect } from 'react'

import { Eye, Download, Plus, MoreVertical } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { accountService } from '@/core/api/services/account/accountService'
import { useInvoices } from '@/core/hooks/account/useInvoices'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/Dropdown/Dropdown'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { Invoice } from '@core/api/entities/invoice/invoice'

import BillingInformation from './BillingInformation'
// import { BillingSummaryCard } from './BillingSummaryCard'
import PaymentMethods from './PaymentMethods'
import ReportInvoiceModal from './ReportInvoiceModal'

interface BillingProps {}

const Billing: React.FC<BillingProps> = () => {
	const { t, currentLanguage } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [isMobile, setIsMobile] = useState(() => {
		if (typeof window === 'undefined') return false
		return window.matchMedia('(max-width: 767px)').matches
	})

	const account = useCurrentAccount()

	const [searchParams, setSearchParams] = useSearchParams()
	const viewParam = searchParams.get('view')

	const [showPaymentMethods, setShowPaymentMethods] = useState(
		viewParam === 'payment-methods'
	)
	const [showBillingInformation, setShowBillingInformation] = useState(
		viewParam === 'billing-details'
	)
	const [showReportInvoiceModal, setShowReportInvoiceModal] = useState(false)
	const [selectedReportInvoiceId, setSelectedReportInvoiceId] = useState<
		number | string | undefined
	>(undefined)
	const [currentPage, setCurrentPage] = useState(1)
	const perPage = 25

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 767px)')
		const onChange = () => setIsMobile(mq.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])

	useEffect(() => {
		if (viewParam === 'payment-methods') {
			setShowPaymentMethods(true)
			setShowBillingInformation(false)
		} else if (viewParam === 'billing-details') {
			setShowPaymentMethods(false)
			setShowBillingInformation(true)
		} else {
			setShowPaymentMethods(false)
			setShowBillingInformation(false)
		}
	}, [viewParam])

	const handleViewChange = (view: string | null) => {
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev)
			if (view) {
				newParams.set('view', view)
			} else {
				newParams.delete('view')
				// Clean up nested params
				newParams.delete('action')
				newParams.delete('method')
			}
			return newParams
		})
	}

	const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoices({
		accountId: account?.id || 0,
		config: useAccountConfig({
			params: {
				include: 'billing_data,currency,integration',
				page: currentPage,
				per_page: perPage,
				order_by: 'created_at',
				direction: 'desc',
			},
		}),
		options: {
			enabled: !!account?.id,
		},
	})

	const invoices = invoicesData?.data || []
	const totalPages = invoicesData?.meta?.pagination?.total_pages || 1

	const formatAmount = (invoice: Invoice): string => {
		const currency = invoice.currency?.iso || 'USD'
		const total = invoice.total || 0
		return `${currency} ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
	}

	const formatDate = (date: string | null): string => {
		if (!date) return '-'

		const dateObj = new Date(date)
		const year = dateObj.getFullYear()
		const month = String(dateObj.getMonth() + 1).padStart(2, '0')
		const day = String(dateObj.getDate()).padStart(2, '0')

		// Check if language is English (en, en-US, etc.)
		const isEnglish = currentLanguage?.toLowerCase().startsWith('en') ?? false

		if (isEnglish) {
			// YYYY-MM-DD format for English
			return `${year}-${month}-${day}`
		} else {
			// DD-MM-YYYY format for other languages
			return `${day}-${month}-${year}`
		}
	}

	const getStatusLabel = (status: string): string => {
		const statusMap: Record<string, string> = {
			draft: t('account.billing.status.draft'),
			open: t('account.billing.status.open'),
			closed: t('account.billing.status.closed'),
			void: t('account.billing.status.void'),
		}
		return statusMap[status] || status
	}

	const handleDownloadPDF = async (invoice: Invoice) => {
		if (!invoice.isDownloadable) return

		try {
			const response = await accountService.downloadInvoice(invoice.id)

			// Check if status is not 200
			if (response.status !== 200) {
				console.error('Download PDF failed with status:', response.status)
				showNotification({
					type: 'error',
					message: t('account.billing.download_error'),
					description: t('account.billing.invoice_not_available'),
				})
				return
			}

			const blob = new Blob([response.data], { type: 'application/pdf' })
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `invoice_${invoice.id}.pdf`
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			window.URL.revokeObjectURL(url)
		} catch (error: any) {
			console.error('Error downloading invoice PDF:', error)
			console.error('Error response status:', error.response?.status)
			console.error('Error response data:', error.response?.data)

			// Handle blob error responses - when responseType is 'blob', errors come as Blob
			let errorMessage = t('account.billing.invoice_not_available')
			let errorTitle = t('account.billing.download_error')

			if (error.response?.data instanceof Blob) {
				try {
					const errorText = await error.response.data.text()
					console.log('Error text from blob:', errorText)
					const errorJson = JSON.parse(errorText)
					console.log('Parsed error JSON:', errorJson)

					// Check for S3 NoSuchKey error (file doesn't exist)
					if (
						errorJson.message?.includes('NoSuchKey') ||
						errorJson.message?.includes('S3: GetObject')
					) {
						errorMessage = t('account.billing.invoice_file_not_found')
					} else {
						errorMessage = errorJson.message || errorJson.error || errorMessage
						// Include additional error details if available
						if (errorJson.errors && typeof errorJson.errors === 'object') {
							const errorDetails = Object.values(errorJson.errors)
								.flat()
								.join(', ')
							if (errorDetails) {
								errorMessage = `${errorMessage}: ${errorDetails}`
							}
						}
					}
				} catch (parseError) {
					console.error('Failed to parse error blob:', parseError)
					// If parsing fails, use default message
				}
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message
			}

			showNotification({
				type: 'error',
				message: errorTitle,
				description: errorMessage,
			})
		}
	}

	const handleViewInNewTab = async (invoice: Invoice) => {
		if (invoice.isDownloadable) {
			try {
				const response = await accountService.downloadInvoice(invoice.id)

				// Check if status is not 200
				if (response.status !== 200) {
					console.error('View PDF failed with status:', response.status)
					showNotification({
						type: 'error',
						message: t('account.billing.download_error'),
						description: t('account.billing.invoice_not_available'),
					})
					return
				}

				const blob = new Blob([response.data], { type: 'application/pdf' })
				const url = window.URL.createObjectURL(blob)
				window.open(url, '_blank')
				// Clean up the URL after a delay to allow the browser to open it
				setTimeout(() => window.URL.revokeObjectURL(url), 100)
			} catch (error: any) {
				console.error('Error opening invoice PDF:', error)
				console.error('Error response status:', error.response?.status)
				console.error('Error response data:', error.response?.data)

				// Handle blob error responses - when responseType is 'blob', errors come as Blob
				let errorMessage = t('account.billing.invoice_not_available')
				let errorTitle = t('account.billing.download_error')

				if (error.response?.data instanceof Blob) {
					try {
						const errorText = await error.response.data.text()
						console.log('Error text from blob:', errorText)
						const errorJson = JSON.parse(errorText)
						console.log('Parsed error JSON:', errorJson)

						// Check for S3 NoSuchKey error (file doesn't exist)
						if (
							errorJson.message?.includes('NoSuchKey') ||
							errorJson.message?.includes('S3: GetObject')
						) {
							errorMessage = t('account.billing.invoice_file_not_found')
						} else {
							errorMessage =
								errorJson.message || errorJson.error || errorMessage
							// Include additional error details if available
							if (errorJson.errors && typeof errorJson.errors === 'object') {
								const errorDetails = Object.values(errorJson.errors)
									.flat()
									.join(', ')
								if (errorDetails) {
									errorMessage = `${errorMessage}: ${errorDetails}`
								}
							}
						}
					} catch (parseError) {
						console.error('Failed to parse error blob:', parseError)
						// If parsing fails, use default message
					}
				} else if (error.response?.data?.message) {
					errorMessage = error.response.data.message
				}

				showNotification({
					type: 'error',
					message: errorTitle,
					description: errorMessage,
				})
			}
		} else if (invoice.xmlIsDownloadable) {
			try {
				const response = await accountService.downloadInvoiceXML(invoice.id)

				// Check if status is not 200
				if (response.status !== 200) {
					console.error('Download XML failed with status:', response.status)
					showNotification({
						type: 'error',
						message: t('account.billing.download_error'),
						description: t('account.billing.invoice_not_available'),
					})
					return
				}

				const blob = new Blob([response.data], { type: 'application/xml' })
				const url = window.URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `invoice_${invoice.id}.xml`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				window.URL.revokeObjectURL(url)
			} catch (error: any) {
				console.error('Error downloading invoice XML:', error)
				console.error('Error response status:', error.response?.status)
				console.error('Error response data:', error.response?.data)

				// Handle blob error responses - when responseType is 'blob', errors come as Blob
				let errorMessage = t('account.billing.invoice_not_available')
				let errorTitle = t('account.billing.download_error')

				if (error.response?.data instanceof Blob) {
					try {
						const errorText = await error.response.data.text()
						console.log('Error text from blob:', errorText)
						const errorJson = JSON.parse(errorText)
						console.log('Parsed error JSON:', errorJson)

						// Check for S3 NoSuchKey error (file doesn't exist)
						if (
							errorJson.message?.includes('NoSuchKey') ||
							errorJson.message?.includes('S3: GetObject')
						) {
							errorMessage = t('account.billing.invoice_file_not_found')
						} else {
							errorMessage =
								errorJson.message || errorJson.error || errorMessage
							// Include additional error details if available
							if (errorJson.errors && typeof errorJson.errors === 'object') {
								const errorDetails = Object.values(errorJson.errors)
									.flat()
									.join(', ')
								if (errorDetails) {
									errorMessage = `${errorMessage}: ${errorDetails}`
								}
							}
						}
					} catch (parseError) {
						console.error('Failed to parse error blob:', parseError)
						// If parsing fails, use default message
					}
				} else if (error.response?.data?.message) {
					errorMessage = error.response.data.message
				}

				showNotification({
					type: 'error',
					message: errorTitle,
					description: errorMessage,
				})
			}
		}
	}

	// Show PaymentMethods subpage if active
	if (showPaymentMethods) {
		return (
			<PaymentMethods
				onBack={() => handleViewChange(null)}
				hideHeader={isMobile}
			/>
		)
	}

	// Show BillingInformation subpage if active
	if (showBillingInformation) {
		return (
			<BillingInformation
				onBack={() => handleViewChange(null)}
				hideHeader={isMobile}
			/>
		)
	}

	return (
		<>
			<div className="space-y-8 ">
				<div className="flex flex-col lg:flex-row gap-4">
					<Button
						variant="outline"
						onClick={() => handleViewChange('payment-methods')}
						className="rounded-full cursor-pointer"
					>
						{t('account.billing.manage_payment_methods')}
					</Button>
					<Button
						variant="outline"
						onClick={() => handleViewChange('billing-details')}
						className="rounded-full cursor-pointer"
					>
						{t('account.billing.see_billing_details')}
					</Button>
				</div>

				{/* Billing History - hide on mobile */}
				<div className="space-y-4 hidden lg:block">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="text-content-tertiary">
									<th className="text-left py-3 px-2">
										{t('account.billing.table.number')}
									</th>
									<th className="text-left py-3 px-2">
										{t('account.billing.table.billing_data')}
									</th>
									<th className="text-left py-3 px-2">
										{t('account.billing.table.description')}
									</th>
									<th className="text-left py-3 px-2">
										{t('account.billing.table.issued')}
									</th>
									<th className="text-left py-3 px-2">
										{t('account.billing.table.status')}
									</th>
									<th className="text-left py-3 px-2">
										{t('account.billing.table.amount')}
									</th>
									<th className="text-left py-3 px-2"></th>
								</tr>
							</thead>
							<tbody>
								{isLoadingInvoices ? (
									<tr>
										<td
											colSpan={7}
											className="py-8 text-center text-content-tertiary"
										>
											{t('common.loading')}
										</td>
									</tr>
								) : invoices.length === 0 ? (
									<tr>
										<td
											colSpan={7}
											className="py-8 text-center text-content-tertiary"
										>
											{t('account.billing.no_invoices')}
										</td>
									</tr>
								) : (
									invoices.map((invoice) => (
										<tr key={invoice.id} className="text-content-tertiary">
											<td className="py-3 px-2">
												{invoice.integration?.invoice_number ||
													`#${invoice.id}`}
											</td>
											<td className="py-3 px-2">
												{invoice.billingData?.officialCompanyName || '-'}
											</td>
											<td className="py-3 px-2">{invoice.description}</td>
											<td className="py-3 px-2">
												{invoice.createdAt
													? formatDate(invoice.createdAt.toISO())
													: '-'}
											</td>
											<td className="py-3 px-2">
												{getStatusLabel(invoice.status)}
											</td>
											<td className="py-3 px-2">{formatAmount(invoice)}</td>
											<td className="py-3 px-2">
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
														>
															<MoreVertical className="h-4 w-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														{(invoice.isDownloadable ||
															invoice.xmlIsDownloadable) && (
															<DropdownMenuItem
																onClick={() => handleViewInNewTab(invoice)}
															>
																<Eye className="mr-2 h-4 w-4" />
																{t(
																	'account.billing.invoice_menu.view_in_new_tab'
																)}
															</DropdownMenuItem>
														)}
														{invoice.isDownloadable && (
															<DropdownMenuItem
																onClick={() => handleDownloadPDF(invoice)}
															>
																<Download className="mr-2 h-4 w-4" />
																{t(
																	'account.billing.invoice_menu.download_as_pdf'
																)}
															</DropdownMenuItem>
														)}
														<DropdownMenuItem
															onClick={() => {
																setSelectedReportInvoiceId(invoice.id)
																setShowReportInvoiceModal(true)
															}}
														>
															<Plus className="mr-2 h-4 w-4" />
															{t('account.billing.invoice_menu.upload')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					{totalPages > 1 && (
						<PaginationControls
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={setCurrentPage}
						/>
					)}
				</div>

				{/* Report Invoice Modal */}
				<ReportInvoiceModal
					isOpen={showReportInvoiceModal}
					onClose={() => {
						setShowReportInvoiceModal(false)
						setSelectedReportInvoiceId(undefined)
					}}
					invoiceId={selectedReportInvoiceId}
				/>
			</div>
		</>
	)
}

export default Billing
